import { forwardRef, ButtonHTMLAttributes } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { Slottable, Slot } from '@radix-ui/react-slot'
import { cn } from '@vgw/tailwind-merger'
import { IconComponent } from '@vgw/multibrand-fe-icon'
import { PulseAnimation } from '@vgw/multibrand-fe-pulse-animation'

const buttonBaseVariants = cva(
  [
    'relative',
    'flex',
    'items-center',
    'justify-center',
    'font-family-display',
    'font-medium',
    'text-base',
    'outline-offset-2',
    'focus:outline-button-outline-ring-focus',
    'before:absolute',
    'before:inset-0',
    'transition-colors',
    'before:transition-colors',
  ],
  {
    variants: {
      size: {
        sm: ['gap-0.5', 'px-3', 'py-1', 'h-8.5'],
        md: ['gap-0.5', 'px-4', 'py-2', 'h-10.5'],
        lg: ['gap-1', 'px-5', 'py-2.5', 'h-11.5'],
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
)

export type ButtonBaseProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonBaseVariants> & {
    variant?: 'primary' | 'secondary' | 'tertiary'
    iconLeft?: IconComponent
    iconRight?: IconComponent
    asChild?: boolean
    isLoading?: boolean
    isPulsating?: boolean
  }

export const ButtonBase = forwardRef<HTMLButtonElement, ButtonBaseProps>(
  (
    {
      children,
      className,
      variant = 'primary',
      size = 'md',
      iconLeft: IconLeft,
      iconRight: IconRight,
      asChild = false,
      disabled = false,
      isLoading = false,
      isPulsating = false,
      type = 'button',
      ...props
    }: ButtonBaseProps,
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button'

    return (
      <Comp
        className={cn(buttonBaseVariants({ size, className }), {
          'animate-pulsate-primary': isPulsating && variant === 'primary',
          'animate-pulsate-secondary': isPulsating && variant === 'secondary',
        })}
        ref={ref}
        type={type}
        disabled={disabled || isLoading}
        {...props}
      >
        {!!IconLeft && <IconLeft className={cn({ invisible: isLoading })} />}

        {!!isLoading && (
          <span className="absolute top-1/2 left-1/2 translate-x-[-50%] translate-y-[-50%]">
            <PulseAnimation />
          </span>
        )}

        <Slottable>
          {isLoading ? <span className="invisible">{children}</span> : children}
        </Slottable>

        {!!IconRight && <IconRight className={cn({ invisible: isLoading })} />}
      </Comp>
    )
  },
)

ButtonBase.displayName = 'ButtonBase'
